import { useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import * as Icons from '@components/Icons';
import {
  NAV,
  SUPER_ONLY_PATHS,
  isSection,
  type NavItem,
} from './nav-config';
import { ActiveCenterProvider } from '@hooks/useActiveCenter';
import { ConfirmProvider } from '@hooks/useConfirm';

// Bottom tab bar: 4 most-used + hamburger trigger
const BOTTOM_TABS: { id: string; path: string; label: string; Icon: React.ComponentType<{ size?: number }> }[] = [
  { id: 'inicio', path: '/admin/inicio', label: 'Inicio', Icon: Icons.Pulse },
  { id: 'bookings', path: '/admin/bookings', label: 'Reservas', Icon: Icons.Book },
  { id: 'calendar', path: '/admin/calendar', label: 'Calendario', Icon: Icons.Calendar },
  { id: 'customers', path: '/admin/customers', label: 'Clientes', Icon: Icons.Users },
];

function initials(email: string): string {
  const parts = email.split('@')[0]?.split(/[._-]/) ?? [];
  const a = parts[0]?.[0] ?? '';
  const b = parts[1]?.[0] ?? '';
  return (a + b).toUpperCase() || email.slice(0, 2).toUpperCase();
}

function toneForEmail(email: string): number {
  let h = 0;
  for (let i = 0; i < email.length; i++) h = (h * 31 + email.charCodeAt(i)) | 0;
  return Math.abs(h) % 5;
}

export function AdminLayout() {
  const { isAuthenticated, logout, hasRole, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  const isSuper = hasRole('superadmin');
  const isMgr = hasRole('center_admin'); // superadmin is NOT isMgr — center_admin only

  // Superadmin is no longer redirected away from operational pages. The
  // per-section centre dropdown (shared via `useActiveCenter`) provides the
  // context; backend reads it from the X-Center-Id header injected by
  // apiFetch.

  // Route guard: redirect center_admin/staff away from superadmin-only pages
  const isOnSuperOnlyRoute = SUPER_ONLY_PATHS.some((p) => location.pathname.startsWith(p));
  if (!isSuper && isOnSuperOnlyRoute) {
    return <Navigate to={isMgr ? '/admin/inicio' : '/admin/historial'} replace />;
  }

  const visibleNav = NAV.filter((entry) => {
    if (isSection(entry)) return true;
    if (entry.superOnly) return isSuper;
    if (entry.mgrAccess || entry.superAccess) {
      return (entry.mgrAccess === true && isMgr) || (entry.superAccess === true && isSuper);
    }
    return true; // no flag = visible to all authenticated roles
  });

  const role = isSuper ? 'superadmin' : isMgr ? 'center admin' : 'staff';
  const userEmail = user?.email ?? '';
  const userInitials = initials(userEmail);
  const userTone = toneForEmail(userEmail);

  const currentNavItem = NAV.find(
    (n): n is NavItem => !isSection(n) && (location.pathname === n.path || location.pathname.startsWith(n.path + '/')),
  );
  const currentLabel = currentNavItem?.label ?? 'Equipomag';

  function goTo(path: string) {
    void navigate(path);
    setMobileNavOpen(false);
  }

  return (
    <ActiveCenterProvider>
    <ConfirmProvider>
    <div className="app">
      {/* ─── Mobile top app bar (under 760px) ──────────────────────────── */}
      <header className="mobile-topbar">
        <button
          className="icon-btn"
          data-variant="ghost"
          onClick={() => { setMobileNavOpen(true); }}
          aria-label="Abrir menú"
        >
          <Icons.Hamburger size={16} />
        </button>
        <div className="mobile-topbar-title">
          <span className="side-brand-mark" style={{ width: 7, height: 7, marginRight: 8 }} />
          {currentLabel}
        </div>
        <span aria-hidden="true" />
      </header>

      {/* ─── Mobile nav drawer ─────────────────────────────────────────── */}
      {mobileNavOpen && (
        <div className="mobile-nav-scrim" onClick={() => { setMobileNavOpen(false); }}>
          <aside className="mobile-nav-drawer" onClick={(e) => { e.stopPropagation(); }}>
            <div className="mobile-nav-head">
              <div className="side-brand">
                <span className="side-brand-mark">E</span>
                <span>Equipomag</span>
              </div>
              <button
                className="icon-btn"
                data-variant="ghost"
                onClick={() => { setMobileNavOpen(false); }}
                aria-label="Cerrar menú"
              >
                <Icons.X size={14} />
              </button>
            </div>
            <nav className="side-nav" style={{ padding: '0 18px 18px' }}>
              {visibleNav.map((entry, idx) => {
                if (isSection(entry)) {
                  return (
                    <div
                      key={`m-sec-${String(idx)}`}
                      className="side-section-label"
                      style={{ marginTop: idx === 0 ? 0 : 8 }}
                    >
                      {entry.section}
                    </div>
                  );
                }
                const Icon = entry.Icon;
                const isActive = location.pathname === entry.path || location.pathname.startsWith(entry.path + '/');
                return (
                  <button
                    key={`m-${entry.id}`}
                    className="side-item"
                    aria-current={isActive ? 'true' : undefined}
                    onClick={() => { goTo(entry.path); }}
                  >
                    <Icon size={16} />
                    <span>{entry.label}</span>
                    {entry.tag && <span className="side-item-tag">{entry.tag}</span>}
                  </button>
                );
              })}
            </nav>
            <div className="side-foot" style={{ padding: '14px 18px', marginTop: 'auto' }}>
              <div className="side-user">
                <div className="avatar" data-tone={userTone}>{userInitials}</div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="side-user-mail">{userEmail}</div>
                  <div className="side-user-role">{role}</div>
                </div>
              </div>
              <button
                className="btn"
                style={{ width: '100%', justifyContent: 'flex-start', marginTop: 10 }}
                onClick={logout}
              >
                <Icons.Logout size={14} /> Cerrar sesión
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* ─── Desktop sidebar ───────────────────────────────────────────── */}
      <aside className="side">
        <div className="side-brand">
          <span className="side-brand-mark">E</span>
          <span>Equipomag</span>
        </div>

        <div className="side-scroll">
          <nav className="side-nav">
            {visibleNav.map((entry, idx) => {
              if (isSection(entry)) {
                return (
                  <div
                    key={`sec-${String(idx)}`}
                    className="side-section-label"
                    style={{ marginTop: idx === 0 ? 0 : 8 }}
                  >
                    {entry.section}
                  </div>
                );
              }
              const Icon = entry.Icon;
              const isActive = location.pathname === entry.path || location.pathname.startsWith(entry.path + '/');
              return (
                <button
                  key={entry.id}
                  className="side-item"
                  aria-current={isActive ? 'true' : undefined}
                  onClick={() => { void navigate(entry.path); }}
                  data-testid={`nav-${entry.id}`}
                >
                  <Icon size={16} />
                  <span>{entry.label}</span>
                  {entry.tag && <span className="side-item-tag">{entry.tag}</span>}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="side-foot">
          <div className="side-user">
            <div className="avatar" data-tone={userTone}>
              {userInitials}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="side-user-mail">{userEmail}</div>
              <div className="side-user-role">{role}</div>
            </div>
          </div>
          <button
            className="btn"
            style={{ width: '100%', justifyContent: 'flex-start' }}
            onClick={logout}
            data-testid="logout-button"
          >
            <Icons.Logout size={14} /> Cerrar sesión
          </button>
        </div>
      </aside>

      <main className="main" id="main-content">
        <Outlet />
      </main>

      {/* ─── Mobile bottom tab bar (under 760px) ───────────────────────── */}
      <nav className="mobile-tabbar" aria-label="Navegación principal móvil">
        {BOTTOM_TABS.map((tab) => {
          const Icon = tab.Icon;
          const isActive = location.pathname === tab.path || location.pathname.startsWith(tab.path + '/');
          return (
            <button
              key={tab.id}
              className="mobile-tab"
              aria-current={isActive ? 'true' : undefined}
              onClick={() => { void navigate(tab.path); }}
            >
              <Icon size={20} />
              <span>{tab.label}</span>
            </button>
          );
        })}
        <button
          className="mobile-tab"
          onClick={() => { setMobileNavOpen(true); }}
          aria-label="Más opciones"
        >
          <Icons.Hamburger size={20} />
          <span>Más</span>
        </button>
      </nav>
    </div>
    </ConfirmProvider>
    </ActiveCenterProvider>
  );
}
